import './phone-actions.ajscss';

import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { IPhoneActionsService } from '@agent/agent-interface/contact-view/phone/phone-actions.service';
import { IOutgoingMailDeprecatedFacade } from '@agent/core/channel-digit/mail/outgoing-mail-deprecated.service';
import { ISmsDeprecatedService } from '@agent/core/channel-digit/sms/sms-deprecated.service';
import { IVideoconferenceService } from '@agent/core/channel-digit/videoconference/videoconference.service';
import { IAppointmentDeprecatedService } from '@agent/core/contact/appointment-deprecated.service';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { ITransferDeprecatedService } from '@agent/core/contact/transfer/transfer-deprecated.service';
import { IConferenceDeprecatedService } from '@agent/core/phone/conference/conference-deprecated.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { ng1Destroy } from '@commons/util/utilities/rx';
import { IComponentController, IComponentOptions, ILogService } from 'angular';
import { i18n } from 'i18next';
import { combineLatest, map } from 'rxjs';

import {
  BookingFacade,
  Conference,
  ConferenceFacade,
  Direction,
  DisplayDeprecatedComponent,
  ListenFacade,
  MediaType,
  MessagingService,
  None,
  Option,
  ProgressFacade,
  Record,
  RecordFacade,
  reduceOrOfBoolArr,
  untilDestroyed,
} from '@contact/upgrade';

import templateUrl from './phone-actions.template.html';
import { AgentBookinPhoneInprocessState } from './state/agent-booking-phone-routing.module';
import { AppointmentService } from '@agent/core/contact/appointment.service';

const MODULE_NAME = 'contactPhoneActions';

export class PhoneActionsCtrl extends DisplayDeprecatedComponent implements IComponentController {
  public name = MODULE_NAME;

  responseId: string;
  parked: boolean = false;
  recordDeleted: boolean = false;
  recordRequest: Option<Record> = None;
  _recordStartable = false;
  _recordResumable = false;
  _recordInterruptable = false;
  _recordCancellable = false;
  _recordInprogress = false;
  _recordDeleted = false;
  _recordRestricted = false;
  _recordRestrictedDisabled = false;
  _oneListenInProgress: boolean = false;
  canStartRecord: boolean = false;
  canStopRecord: boolean = false;
  canRestrictRecord = false;
  canRedirectToScriptAnchor: boolean = false;
  canTransfer: boolean = false;
  hasTransferPermission: boolean = false;
  currentDeletionMessage: string;
  hasUsecases: boolean = false;
  public hangUpDisable: boolean = false;
  isAGuest: boolean = false;

  constructor(
    protected readonly $log: ILogService,
    protected readonly $i18next: i18n,
    protected readonly contactDeprecatedService: IContactDeprecatedService,
    protected readonly phoneActions: IPhoneActionsService,
    protected readonly conferenceDeprecatedService: IConferenceDeprecatedService,
    protected readonly transferDeprecatedService: ITransferDeprecatedService,
    protected readonly sessionDeprecatedService: SessionDeprecatedService,
    protected readonly recordFacade: RecordFacade,
    protected readonly listenFacade: ListenFacade,
    protected readonly contactFacade: BookingFacade,
    protected readonly smsDeprecatedService: ISmsDeprecatedService,
    protected readonly appointmentDeprecatedService: IAppointmentDeprecatedService,
    protected readonly appointmentService: AppointmentService,
    protected readonly toastDeprecatedService: ToastDeprecatedService,
    protected readonly videoconferenceService: IVideoconferenceService,
    protected readonly outgoingMailDeprecatedFacade: IOutgoingMailDeprecatedFacade,
    protected readonly messaging: MessagingService,
    protected readonly router: AgentDesktopRouter,
    protected readonly progressFacade: ProgressFacade,
    protected readonly conferenceFacade: ConferenceFacade
  ) {
    super();
  }

  get isRecordStartable(): boolean {
    return this._recordStartable;
  }

  get isRecordResumable(): boolean {
    return this._recordResumable;
  }

  get isRecordInterruptable(): boolean {
    return this._recordInterruptable;
  }

  get isRecordCancellable(): boolean {
    return this._recordCancellable;
  }

  get isRecordInprogress(): boolean {
    return this._recordInprogress;
  }

  get isRecordDeleted(): boolean {
    return this._recordDeleted;
  }

  get isRecordRestricted(): boolean {
    return this._recordRestricted;
  }

  get isListenInProgress(): boolean {
    return this._oneListenInProgress;
  }

  get recordInterrupted(): boolean {
    return this.recordRequest && this.recordRequest.isDefined() && !this.recordRequest.get().inprogress;
  }

  $onInit(): void {
    const record$ = this.recordFacade.records$.pipe(map(records => records.find(({ responseId }) => responseId === this.responseId)));
    record$.pipe(ng1Destroy(this)).subscribe(record => (this.recordRequest = Option.of(record)));
    record$.pipe(ng1Destroy(this)).subscribe(record => (this.recordDeleted = !!record && record.deleted));

    this.conferenceFacade
      .getById(this.responseId)
      .pipe(untilDestroyed(this))
      .subscribe(conference => {
        this.isAGuest = conference?.role === 'guest';
      });
    this.recordFacade
      .getByResponseId(this.responseId)
      .pipe(ng1Destroy(this))
      .subscribe(record => {
        this._recordStartable = record?.startable ?? false;
        this._recordResumable = record?.resumable ?? false;
        this._recordInterruptable = record?.interruptable ?? false;
        this._recordCancellable = record?.cancellable ?? false;
        this._recordInprogress = record?.inprogress ?? false;
        this._recordDeleted = record?.deleted ?? false;
        this._recordRestricted = record?.restricted ?? false;
      });
    this.listenFacade
      .getByResponseId(this.responseId)
      .pipe(ng1Destroy(this))
      .subscribe(listen => (this._oneListenInProgress = listen?.inprogress ?? false));

    this.currentDeletionMessage = 'Request from an active call';
    this.parked = this.conferenceDeprecatedService.isContactParked(this.responseId);

    // if there is a transfer, go to transfer routing state.
    this.transferDeprecatedService.getTransfer(this.responseId).forEach(transfer => {
      transfer;
      this.transfer();
    });

    const setRestrictedShow$ = this.progressFacade.isShow('record#restrict');
    const unsetRestrictedShow$ = this.progressFacade.isShow('record#unrestrict');
    combineLatest([setRestrictedShow$, unsetRestrictedShow$])
      .pipe(reduceOrOfBoolArr(), ng1Destroy(this))
      .subscribe(show => {
        this._recordRestrictedDisabled = show;
      });

    this.videoconferenceService.getUsecases().then(
      () => (this.hasUsecases = true),
      () => (this.hasUsecases = false)
    );

    this.contactDeprecatedService.getContact(this.responseId).then(contact => {
      let directionName: string = Direction[contact.channel.direction];
      const mediaTypeName: string = MediaType[contact.channel.type];
      this.canStartRecord = this.sessionDeprecatedService.hasPermission(
        'CSR_' + mediaTypeName + '_' + directionName + '::csr_record:start'
      );
      this.canRestrictRecord = this.sessionDeprecatedService.hasPermission(
        'CSR_' + mediaTypeName + '_' + directionName + '::csr_record:restrict'
      );
      this.canStopRecord = this.sessionDeprecatedService.hasPermission('CSR_' + mediaTypeName + '_' + directionName + '::csr_record:stop');
      this.hasTransferPermission = this.sessionDeprecatedService.hasPermission(
        'CSR_' +
          mediaTypeName +
          '_' +
          directionName +
          '::contact:transfer|contact:transfer_to_directory|contact:transfer_to_free_number|contact:simple_redirect' +
          '|contact:accompanied_redirect|contact:transfer_to_skill|contact:transfer_to_skill_unitary_phone_outgoing|contact:transfer_to_external_center|contact:transfer_to_team' +
          '|contact:transfer_to_skill_phone_' +
          directionName.toLowerCase()
      );
      // when receiving a transferred contact cannot re transfer him until transfer is committed by source agent
      // ODECSCNGACCEPTANCE-11836 cf jira
      this.canTransfer = !contact.conference && this.hasTransferPermission;
      // Currently on phone incoming, phone outgoing and unitary phone outgoing channels
      this.canRedirectToScriptAnchor =
        (directionName === 'INCOMING' &&
          this.sessionDeprecatedService.hasPermission(
            'CSR_PHONE_INCOMING::contact:redirect_to_script_anchor_with_csr_release|contact:redirect_to_script_anchor_with_csr_park'
          )) ||
        (directionName === 'OUTGOING' &&
          this.sessionDeprecatedService.hasPermission(
            'CSR_PHONE_OUTGOING::contact:redirect_to_script_anchor_with_csr_release|contact:redirect_to_script_anchor_with_csr_park'
          )) ||
        (directionName === 'OUTGOING' &&
          this.sessionDeprecatedService.hasPermission(
            'CSR_UNITARY_PHONE_OUTGOING::contact:redirect_to_script_anchor_with_csr_release|contact:redirect_to_script_anchor_with_csr_park'
          ));
    });

    this.contactFacade.bookings$
      .pipe(
        ng1Destroy(this),
        map(contacts =>
          contacts.filter(
            contact =>
              (contact.channel.type === MediaType.UNITARY_PHONE || contact.channel.type === MediaType.PHONE) &&
              contact.responseId === this.responseId
          )
        )
      )
      .subscribe(contacts =>
        contacts.forEach(contact => {
          this.canTransfer = !contact.conference && this.hasTransferPermission;
        })
      );
  }

  hangUp(): void {
    this.hangUpDisable = true;
    setTimeout(() => {
      this.hangUpDisable = false;
    }, 5000);
    this.contactDeprecatedService.getContact(this.responseId).then(
      contact => {
        this.phoneActions.hangUp(contact);
        // if interaction finish from campaign => call getTodayAppointmentNumber to update appointmentCounter
        if (contact.campaign?.campaignContactId) {
          this.appointmentService.getTodayAppointmentNumber();
        }
      },
      error => {
        this.$log.error(error);
      }
    );
  }

  park(): void {
    this.contactDeprecatedService.getContact(this.responseId).then(
      contact => {
        this.phoneActions.parkContact(contact).then(() => {
          this.parked = this.conferenceDeprecatedService.isContactParked(this.responseId);
        });
      },
      error => {
        this.$log.error(error);
      }
    );
  }

  unpark(): void {
    this.contactDeprecatedService.getContact(this.responseId).then(
      contact => {
        this.phoneActions.unparkContact(contact).then(() => {
          this.parked = this.conferenceDeprecatedService.isContactParked(this.responseId);
        });
      },
      error => {
        this.$log.error(error);
      }
    );
  }

  transfer() {
    this.router.navigateFromLastChild(['..', AgentBookinPhoneInprocessState.forward]);
  }

  scriptRedirect() {
    this.router.navigateFromLastChild(['..', AgentBookinPhoneInprocessState.scriptRedirection]);
  }

  startRecord() {
    this.recordRequest.forEach(({ id }) => this.recordFacade.start(id));
  }

  stopRecord() {
    this.recordRequest.forEach(({ id }) => this.recordFacade.askToPreventConfirm(id));
  }

  setRecordRestricted() {
    this.recordRequest.forEach(({ id }) => this.recordFacade.setRestricted(id));
  }

  unsetRecordRestricted() {
    this.recordRequest.forEach(({ id }) => this.recordFacade.unsetRestricted(id));
  }

  interruptRecord() {
    this.recordRequest.forEach(({ id }) => this.recordFacade.interrupt(id));
  }

  resumeRecord() {
    this.recordRequest.forEach(({ id }) => this.recordFacade.resume(id));
  }

  openSMSForm(): void {
    this.contactDeprecatedService.getSelectedContact().then(contact => {
      contact.customerId && this.smsDeprecatedService.openSMSForContact(contact.customerId);
    });
  }

  canOpenVCForm(): boolean {
    return !this.videoconferenceService.codeSent && this.hasUsecases;
  }

  openVCForm(): void {
    this.videoconferenceService.openVCModal();
  }

  openAppointmentForm(): void {
    this.contactDeprecatedService.getSelectedContact().then(contact => {
      this.contactDeprecatedService.getCustomer(contact.responseId).then(customer => {
        let firstName: string;
        let lastName: string;
        if (!!customer) {
          firstName = customer.firstname;
          lastName = customer.lastname;
        } else {
          firstName = contact.customerName;
          lastName = '';
        }
        this.appointmentDeprecatedService.openAppointmentForm(contact.responseId, contact.phoneNumber, firstName, lastName, contact.skills);
      });
    });
  }

  canOpenUnitMail(): boolean {
    return this.outgoingMailDeprecatedFacade.canOpenOutgoingMail();
  }

  openUnitMail(): void {
    this.contactDeprecatedService.getSelectedContact().then(contact => {
      this.outgoingMailDeprecatedFacade.openOutgoingMailForContactUuid(
        Option.of(contact.customerId).getOrElse(contact.customerId || ''),
        this.responseId
      );
    });
  }
}

export class PhoneActions implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    responseId: '<',
  } as { [binding: string]: string };
  controller = [
    '$log',
    '$i18next',
    'contactDeprecatedService',
    'phoneActions',
    'conferenceDeprecatedService',
    'transferDeprecatedService',
    'sessionDeprecatedService',
    'recordFacade',
    'listenFacade',
    'contactFacade',
    'smsDeprecatedService',
    'appointmentDeprecatedService',
    'appointmentService',
    'toastDeprecatedService',
    'videoconference',
    'outgoingMailDeprecatedService',
    'messaging',
    'agentDesktopRouter',
    'progressFacade',
    'conferenceFacade',
    PhoneActionsCtrl,
  ];
}

export default MODULE_NAME;
